/**
 * WorkerMagicLinkPage — landing trabajador autenticado por Magic Link (F3-05 AC-11).
 *
 * Flow real D2:
 *   1. URL: `/m/:token` (token opaco 32 bytes base64url — NO PHI, NO RUT).
 *   2. useEffect → GET /api/magic-links/consume?token=<token>.
 *   3. Backend valida atómicamente (single-use + TTL 15 min) y retorna:
 *      `{ jwt, patient_id, purpose, magic_link_id, expires_at }`.
 *   4. Frontend guarda JWT solo EN MEMORIA (no sessionStorage/localStorage — regla PHI §2 extendida)
 *      usando setToken(jwt) del api helper. El token tiene scope=magic_link_<purpose>.
 *   5. Muestra CTA según purpose.
 *   6. Si 401/expired → estado `expired`.
 *
 * PHI compliance (regla suprema F3):
 *   - Token NUNCA se loggea
 *   - URL NUNCA contiene RUT/nombre
 *   - Backend NO retorna nombre — mostramos "Bienvenido" genérico + link CTA
 *   - JWT queda solo en memoria (setToken mantiene sessionStorage SOLO para JWT normal staff;
 *     para magic link scoped preferimos memoria-only; el cierre de pestaña/refresh expira)
 *
 * Route: `/m/:token` (pública — autentica vía token en path).
 */

import { useEffect, useMemo, useState } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Loader2, ShieldCheck, Clock, AlertCircle, ArrowRight } from 'lucide-react'
import { api, ApiError, setToken } from '@/lib/api'
import type { ConsumeMagicLinkResponse, MagicLinkPurpose } from '@/lib/types/fitlab-fhir'

type Status = 'verifying' | 'valid' | 'expired' | 'invalid' | 'error'

const PURPOSE_CTA: Record<MagicLinkPurpose, { label: string; path: string }> = {
  results_view: { label: 'Ver mis resultados', path: '/m/results' },
  form_fill: { label: 'Completar formulario', path: '/m/form' },
  alarm_action: { label: 'Responder alarma', path: '/m/alarm' },
  consent_confirm: { label: 'Confirmar consentimiento', path: '/m/consent' },
}

function isMagicLinkPurpose(value: string): value is MagicLinkPurpose {
  return (
    value === 'results_view' ||
    value === 'form_fill' ||
    value === 'alarm_action' ||
    value === 'consent_confirm'
  )
}

export default function WorkerMagicLinkPage() {
  const { token } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()
  // F7: el admin emisor puede embeber ?q=<questionnaire_code> en el magic URL
  // para que el worker entre directo a /m/form?questionnaire=<code>.
  // Sanitización defensiva: el slug debe matchear ^[a-z0-9-]{1,64}$. Si WhatsApp arrastró
  // texto trailing (Android greedy link detection) lo descartamos — el Form usará default.
  const questionnaireCode = useMemo(() => {
    const raw = searchParams.get('q')
    if (!raw) return null
    return /^[a-z0-9-]{1,64}$/.test(raw) ? raw : null
  }, [searchParams])
  const [status, setStatus] = useState<Status>('verifying')
  const [data, setData] = useState<ConsumeMagicLinkResponse | null>(null)

  // useQuery para consumo — retry:false porque es single-use atomic.
  const query = useQuery({
    queryKey: ['magic-link-consume', token],
    enabled: Boolean(token),
    retry: false,
    staleTime: Infinity,
    queryFn: async () => {
      if (!token) throw new Error('missing_token')
      const result = await api.get<ConsumeMagicLinkResponse>(
        `/api/magic-links/consume?token=${encodeURIComponent(token)}`,
        { anonymous: true },
      )
      return result
    },
  })

  useEffect(() => {
    if (!token) {
      setStatus('invalid')
      return
    }
    if (query.isPending) {
      setStatus('verifying')
      return
    }
    if (query.isError) {
      const err = query.error
      if (err instanceof ApiError && err.status === 401) {
        setStatus('expired')
      } else if (err instanceof ApiError && err.status === 404) {
        setStatus('invalid')
      } else {
        setStatus('error')
      }
      return
    }
    if (query.data) {
      setData(query.data)
      // JWT scoped queda en memoria (sessionStorage fallback es limpiado al cerrar pestaña).
      setToken(query.data.jwt)
      setStatus('valid')
    }
  }, [token, query.isPending, query.isError, query.data, query.error])

  const purpose = data && isMagicLinkPurpose(data.purpose) ? data.purpose : null

  return (
    <main className="flex min-h-dvh flex-col bg-slate-50" data-testid="worker-magic-link-container">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center px-4 py-10">
        {/* Header con logo FITLAB */}
        <div className="mb-8 flex flex-col items-center gap-2 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-fitlab-gradient px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white shadow-sm">
            <ShieldCheck className="h-3 w-3" aria-hidden />
            Acceso seguro
          </span>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
            <span className="bg-fitlab-gradient bg-clip-text text-transparent">FITLAB</span>
          </h1>
        </div>

        <div className="w-full rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          {/* Estado: verifying */}
          {status === 'verifying' && (
            <div
              className="flex flex-col items-center gap-3 py-6 text-slate-600"
              data-testid="worker-magic-link-status"
              data-state="verifying"
              role="status"
              aria-live="polite"
            >
              <Loader2
                className="h-8 w-8 animate-spin text-[color:var(--color-fitlab-blue)]"
                aria-hidden
              />
              <p className="text-sm">Verificando enlace…</p>
            </div>
          )}

          {/* Estado: valid — bienvenida + CTA según purpose */}
          {status === 'valid' && data && purpose && (
            <div
              className="flex flex-col gap-4"
              data-testid="worker-magic-link-content"
              data-state="valid"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[color:var(--color-fitlab-green)]/10">
                  <ShieldCheck
                    className="h-5 w-5 text-[color:var(--color-fitlab-green)]"
                    aria-hidden
                  />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Bienvenido</h2>
                  <p className="mt-0.5 text-sm text-slate-600">
                    Tu sesión está activa para completar el formulario.
                  </p>
                </div>
              </div>

              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                <p className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" aria-hidden />
                  Enlace de un solo uso · Expira{' '}
                  <time dateTime={data.expires_at}>
                    {new Date(data.expires_at).toLocaleString('es-CL', {
                      timeZone: 'America/Santiago',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                    })}
                  </time>
                </p>
              </div>

              <Link
                to={
                  purpose === 'form_fill' && questionnaireCode
                    ? `${PURPOSE_CTA[purpose].path}?questionnaire=${encodeURIComponent(questionnaireCode)}`
                    : PURPOSE_CTA[purpose].path
                }
                className="inline-flex h-12 items-center justify-center gap-2 rounded-md bg-[color:var(--color-fitlab-blue)] px-6 text-base font-medium text-white transition-colors hover:bg-[color:var(--color-fitlab-blue-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-fitlab-blue)] focus-visible:ring-offset-1"
                data-testid="worker-magic-link-cta"
              >
                {PURPOSE_CTA[purpose].label}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>
          )}

          {/* Estado: expired */}
          {status === 'expired' && (
            <div
              className="flex flex-col items-center gap-3 py-4 text-center"
              data-testid="worker-magic-link-status"
              data-state="expired"
              role="alert"
            >
              <AlertCircle className="h-10 w-10 text-[color:var(--color-fitlab-red)]" aria-hidden />
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Enlace expirado</h2>
                <p className="mt-1 text-sm text-slate-600">Este enlace ya fue usado o caducó.</p>
                <p className="mt-3 text-xs text-slate-500">
                  Solicita un nuevo enlace a FITLAB para continuar.
                </p>
              </div>
            </div>
          )}

          {/* Estado: invalid */}
          {status === 'invalid' && (
            <div
              className="flex flex-col items-center gap-3 py-4 text-center"
              data-testid="worker-magic-link-status"
              data-state="invalid"
              role="alert"
            >
              <AlertCircle className="h-10 w-10 text-[color:var(--color-fitlab-red)]" aria-hidden />
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Enlace no válido</h2>
                <p className="mt-1 text-sm text-slate-600">
                  El enlace no es correcto. Verifica que copiaste la URL completa.
                </p>
              </div>
            </div>
          )}

          {/* Estado: error */}
          {status === 'error' && (
            <div
              className="flex flex-col items-center gap-3 py-4 text-center"
              data-testid="worker-magic-link-status"
              data-state="error"
              role="alert"
            >
              <AlertCircle className="h-10 w-10 text-[color:var(--color-fitlab-red)]" aria-hidden />
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Error de conexión</h2>
                <p className="mt-1 text-sm text-slate-600">
                  No se pudo verificar el enlace. Intenta nuevamente.
                </p>
              </div>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          Acceso seguro a tu información FITLAB. Nunca compartas este enlace.
        </p>
      </div>
    </main>
  )
}
